import { Rect } from "../../drawing";
import { GregorioSegment } from "../../gregorio-chant/structures/GregorioSegment";
import { ChantContext } from "../../gregorio-chant/ChantContext";
import { ChantLayoutElement } from "../layout/ChantLayoutElement";
import { NabcSnippetVisualizer } from "./NabcSnippetVisualizer";
import { GregorioEndOfLineInfo } from "../../gregorio-chant/structures/GregorioSyllable";
import { Space } from "../elements/Space";
import { GlyphVisualizer } from "../visualizers";
import { VisualizerHelper } from "../visualizers/VisualizerHelper";
import { Clef } from "../clefs";
import { Custos } from "../signs";
import { GregorioType } from "../../gregorio-chant/enums/GregorioType";
import { SyllableBreakpoint } from "./SyllableBreakpoint";
import { isBarVisualizer, isSign } from "../visualizers/helpers";
import { min } from "lodash";
import { HorizontalEpisema } from "../visualizers/HorizontalEpisema";
import { Mora } from "../visualizers/Mora";

type NoBreakRule = (
  curr: ChantLayoutElement,
  next: ChantLayoutElement[],
  prev: ChantLayoutElement[]
) => boolean;
const noBreakRules: NoBreakRule[] = [
  (curr, next) => next.length > 0 && next[0] instanceof Space,
  (curr, next) =>
    (next.length > 0 && next.every((n) => n instanceof Space || isBarVisualizer(n))) ?? false,
  (curr) => curr instanceof GlyphVisualizer && curr.isAlternation,
  (curr, next, prev) =>
    curr instanceof Space &&
    prev.length > 0 &&
    prev[prev.length - 1] instanceof GlyphVisualizer &&
    (prev[prev.length - 1] as GlyphVisualizer).isAlternation,
  (curr, next) => (next.length > 0 && isSign(next[0])) ?? false,
  (curr, next) => next.length > 0 && next[0] instanceof GlyphVisualizer && next[0].isQuilisma,
  (curr, next) =>
    next.length > 0 && next[0] instanceof GlyphVisualizer && next[0].isPunctumInclinatum,
  (curr) => curr instanceof Mora,
];

export class ChantSegmentElement extends ChantLayoutElement {
  protected _visualizers: ChantLayoutElement[] = [];
  protected _nabcNuemes: NabcSnippetVisualizer[] = [];
  protected _segment: GregorioSegment;
  protected _endOfLine?: GregorioEndOfLineInfo;
  // protected _debug = true;

  get hasNabc() {
    return this._segment.nabcNotations.length > 0;
  }

  get hasCustos() {
    return this._visualizers.some((v) => v instanceof Custos);
  }

  get custos() {
    return this._visualizers.find((v) => v instanceof Custos) as Custos;
  }

  get hasClef() {
    return this._visualizers.some((v) => v instanceof Clef);
  }

  get hasHepisema() {
    return this._visualizers.some((v) => v instanceof HorizontalEpisema);
  }

  get clef() {
    return this._visualizers.find((v) => v instanceof Clef) as Clef;
  }

  get isClef() {
    return this._segment.elements.every((e) => {
      return e.type === GregorioType.CLEF;
    });
  }

  get isBar() {
    return this._segment.elements.every((e) => {
      return e.type === GregorioType.BAR;
    });
  }

  get isBarFirst() {
    return this._segment.elements.length > 0 && this._segment.elements[0].type === GregorioType.BAR;
  }

  get segment() {
    return this._segment;
  }

  get hasEndOfLine() {
    return !!this._endOfLine;
  }

  get endOfLineInfo() {
    return this._endOfLine;
  }

  set endOfLineInfo(info: GregorioEndOfLineInfo | undefined) {
    this._endOfLine = info;
  }

  get visualizers() {
    return this._visualizers;
  }

  get noteVisualizers() {
    return this._visualizers.filter(
      (e) => e instanceof GlyphVisualizer && e.notes.length > 0
    ) as GlyphVisualizer[];
  }

  set isLastSyllable(value: boolean) {
    this._isLastSyllable = value;
  }

  get isLastSyllable() {
    return this._isLastSyllable;
  }

  get top() {
    return Math.min(
      min(this._visualizers.map((e) => e.top)) || 0,
      min(this._nabcNuemes.map((e) => e.top)) || 0
    );
  }

  get maxY(): number {
    return min(this._visualizers.map((v) => v.bounds.y)) || 0;
  }

  private _isLastSyllable = false;

  constructor(context: ChantContext, segment: GregorioSegment) {
    super(context);
    this._segment = segment;
  }

  addVisualizer(chantLayoutElement: ChantLayoutElement) {
    this._visualizers.push(chantLayoutElement);
  }

  addVisualizers(chantLayoutElements: ChantLayoutElement[]) {
    this._visualizers.push(...chantLayoutElements);
  }

  popVisualizer() {
    return this._visualizers.pop();
  }

  get firstVisualizer() {
    return this._visualizers[0];
  }

  get lastVisualizer() {
    return this._visualizers[this._visualizers.length - 1];
  }

  popInterglyphSpace() {
    if (this._visualizers.length === 0) {
      return;
    }
    const last = this._visualizers[this._visualizers.length - 1];
    if (
      last instanceof Space &&
      last.bounds.width === this._distanceConverter.lengthToPx(this._context.interglyphspace)
    ) {
      return this._visualizers.pop();
    }
  }

  addNabcVisualizer(nueme: NabcSnippetVisualizer) {
    this._nabcNuemes.push(nueme);
  }

  addNabcVisualizers(nuemes: NabcSnippetVisualizer[]) {
    this._nabcNuemes.push(...nuemes);
  }

  // addInterelementSpace() {
  //   if (this._visualizers.length === 0 || this._visualizers[this._visualizers.length - 1] instanceof Space) {
  //     return;
  //   }
  //   this._visualizers.push(Space.createFromSpace(this._context, GregorioSpace.INTERELEMENT_SPACE));
  // }

  // addInterglyphSpace() {
  //   this._visualizers.push(Space.createFromSpace(this._context, GregorioSpace.INTERGLYPH_SPACE));
  // }

  performLayout(notationBounds: Rect, renderNabc: boolean) {
    let bounds = Rect.create(0, notationBounds.y, 0, notationBounds.height);

    if (!this._context.nabcOnlyMode) {
      for (let i = 0; i < this._visualizers.length; i++) {
        const visualizer = this._visualizers[i];
        this._visualizers[i].performLayout(this._visualizers.slice(0, i));
        bounds = bounds.horizontalUnion(visualizer.bounds);
      }
      bounds.setX(0);
    }

    if (this.hasNabc && renderNabc) {
      this._nabcNuemes.forEach((visualizer) => {
        visualizer.performLayout();
        visualizer.bounds.setX(0);
        if (this._context.nabcOnlyMode) {
          visualizer.bounds.moveY(this._context.nabcOnlyModeNabcLineBaselineY);
          bounds = bounds.horizontalUnion(visualizer.bounds);
        }
      });
    }

    this.bounds = bounds;
  }

  createSvgFragments(): SVGElement[] {
    const rootElement = document.createElementNS("http://www.w3.org/2000/svg", "g");
    rootElement.setAttribute("class", "ChantNotationElement " + this.constructor.name);
    rootElement.setAttribute("transform", "translate(" + this.bounds.x + "," + 0 + ")");

    if (!this._context.nabcOnlyMode) {
      for (const visualizer of this._visualizers) {
        if ((visualizer instanceof Clef || visualizer instanceof Custos) && this.isLastSyllable) {
          continue;
        }
        for (const fragment of visualizer.createSvgFragments()) {
          rootElement.appendChild(fragment);
        }
      }
    }

    if (this.hasNabc) {
      const nabcRootElement = document.createElementNS("http://www.w3.org/2000/svg", "g");
      nabcRootElement.setAttribute("class", "nabc");
      const baseline =
        this._context.nabcLineHeight +
        this._distanceConverter.lengthToPx(this._context.abovelinestextraise);
      nabcRootElement.setAttribute("transform", `translate(0, ${-baseline})`);

      for (const visualizer of this._nabcNuemes) {
        for (const fragment of visualizer.createSvgFragments()) {
          nabcRootElement.appendChild(fragment);
        }
      }

      rootElement.appendChild(nabcRootElement);
    }

    return [rootElement, ...super.createSvgFragments()];
  }

  getHeightForCustos(): number {
    const visualizer = VisualizerHelper.findFirstGlyphVisualizer(this._visualizers);
    if (!visualizer) {
      return 0;
    }
    return visualizer.bounds.y;
  }

  // split(visualizerIndex: number): [ChantSegmentElement, ChantSegmentElement] {
  //   const [segment1, segment2] = this.segment.split(elementIndex);
  //   // const segmentEl1 = new ChantSegmentElement(this._context, segment1);
  //   // const segmentEl2 = new ChantSegmentElement(this._context, segment2);
  //   // return [segmentEl1, segmentEl2];
  // }

  getBreakpoints(segmentIndex: number): SyllableBreakpoint[] {
    const result: SyllableBreakpoint[] = [];
    for (let i = 1; i < this._visualizers.length; i++) {
      const visualizer = this._visualizers[i];
      // const nextVisualizer = this._visualizers[i + 1];

      if (
        noBreakRules.every(
          (rule) => !rule(visualizer, this.visualizers.slice(i + 1), this.visualizers.slice(0, i))
        )
      ) {
        result.push({
          segmentIndex: segmentIndex,
          visualizerIndex: i,
          right: visualizer.bounds.right,
        });
      }
    }

    return result;
  }

  split(
    visualizerIndex: number
  ): [ChantSegmentElement | undefined, ChantSegmentElement | undefined] {
    let segmentEl1,
      segmentEl2 = undefined;
    const [gregSegment1, gregSegment2] = this._segment.split(visualizerIndex + 1);

    const visualizers1 = this._visualizers.slice(0, visualizerIndex + 1);
    if (visualizers1.length > 0) {
      segmentEl1 = new ChantSegmentElement(this._context, gregSegment1);
      segmentEl1.addVisualizers(visualizers1);
      if (this.hasNabc) {
        segmentEl1.addNabcVisualizers(this._nabcNuemes);
      }
    }

    const visualizers2 = this._visualizers.slice(visualizerIndex + 1);

    if (visualizers2.length > 0) {
      segmentEl2 = new ChantSegmentElement(this._context, gregSegment2);
      segmentEl2.addVisualizers(visualizers2);
      if (this.hasNabc && segmentEl1 === undefined) {
        segmentEl2.addNabcVisualizers(this._nabcNuemes);
      }
    }

    return [segmentEl1, segmentEl2];
  }
}
